import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Bill,
    Convert,
    DocumentText,
    Fatrows,
    GlobalEdit,
    Message,
    MessageQuestion,
    ReceiptItem,
    Send2,
    WalletAdd,
} from "iconsax-reactjs";
import { handleFetchWalletTransactions } from "../../api/fetchers";
import { formatTransactions } from "../../utilities/transactionUtils";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import TransactionDetailsModal from "../wallet/TransactionDetailsModal";
import Loader from "../loader";

export default function RecentTransactions({ DayBlock, Divider, TxRow, EmptyState }) {
    const navigate = useNavigate();
    const { business, currency } = useSelector((s) => s);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);

    const typeToIcon = useMemo(
        () => ({
            "wallet-funding": <WalletAdd size="18" color="#6F739B" variant="Bulk" />,
            "wallet-conversion": <Convert size="18" color="#6F739B" variant="Bulk" />,
            "local-transfer": <Send2 size="18" color="#6F739B" variant="Bulk" />,
            "cross-border-transfer": <GlobalEdit size="18" color="#6F739B" variant="Bulk" />,
            "otc-transfer": <GlobalEdit size="18" color="#6F739B" variant="Bulk" />,
            "invoice-payments": <ReceiptItem size="18" color="#6F739B" variant="Bulk" />,
            "order-payments": <DocumentText size="18" color="#6F739B" variant="Bulk" />,
            "shipment-payments": <Message size="18" color="#6F739B" variant="Bulk" />,
            "bill-payment": <Bill size="18" color="#6F739B" variant="Bulk" />,
            finance: <Fatrows size="18" color="#6F739B" variant="Bulk" />,
            others: <MessageQuestion size="18" color="#6F739B" variant="Bulk" />,
        }),
        []
    );
    useEffect(() => {
        if (!business?._id) return;
        (async () => {
            setLoading(true);
            try {
                const payload = await handleFetchWalletTransactions({
                    businessId: business._id,
                    page: 1,
                    limit: 5,
                    status: [],
                    transactionType: [],
                    fromDate: undefined,
                    toDate: undefined,
                    currency,
                    searchTerm: undefined,
                    order: "desc",
                });

                const raw = payload?.data || [];
                const formatted = formatTransactions(raw);

                const top5 = [...formatted]
                    .sort((a, b) => new Date(b.time) - new Date(a.time))
                    .slice(0, 5);
                const adapted = top5.map((t) => ({
                    id: t.id,
                    title: t.title,
                    timeISO: t.time,
                    status: t.status || "Completed",
                    amount: t.amount ?? 0,
                    currency: t.currency,
                    direction: t.paymentType === "credit" ? "in" : "out",
                    icon: typeToIcon[t.transactionType] || typeToIcon.others,
                    transactionType: t.transactionType,
                    full: t,
                }));

                setItems(adapted);
            } catch (e) {
                console.error("Failed to fetch recent transactions", e);
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [business?._id, currency, typeToIcon]);

    const blocks = useMemo(() => groupByCalendarDay(items), [items]);

    return (
        <>
            <div className="flex justify-between items-center mt-1 text-[#101828] text-lg">
                <span>Recent transactions</span>
                <div
                    className="flex cursor-pointer items-center"
                    onClick={() => navigate("/wallets")}
                >
                    <span>View all</span>
                    <ChevronRight />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center h-[80%] items-center">
                    <button className="bg-[#6F00FF] py-2 px-6 rounded-xl">
                        <Loader />
                    </button>
                </div>
            ) : blocks.length === 0 ? (
                <EmptyState label="No recent transactions" />
            ) : (
                <div className="mt-4  h-[50vh] hide-scrollbar overflow-scroll">
                    {blocks.map(({ label, items }) => (
                        <DayBlock key={label} label={label}>
                            {items.map((tx, i) => (
                                <React.Fragment key={tx.id || i}>
                                    <div
                                        role="button"
                                        onClick={() => {
                                            setSelectedTxn(tx.full);
                                            setIsDrawerOpen(true);
                                        }}
                                        className="rounded-lg -mx-2 px-2 hover:bg-[#F8F9FB] cursor-pointer"
                                    >
                                        <TxRow {...tx} />
                                    </div>
                                    {i !== items.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </DayBlock>
                    ))}
                </div>
            )}

            <TransactionDetailsModal
                isOpen={isDrawerOpen}
                txn={selectedTxn}
                typeToIcon={typeToIcon}
                onClose={() => setIsDrawerOpen(false)}
                containerClassName=""
                panelClassName="lg:w-[510px] lg:rounded-tl-3xl lg:rounded-bl-2xl"
            />
        </>
    );
}

function groupByCalendarDay(items) {
    if (!Array.isArray(items) || items.length === 0) return [];
    const map = new Map();
    items.forEach((it) => {
        const key = (it?.timeISO && it.timeISO.slice(0, 10)) || "unknown";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(it);
    });
    const ordered = [...map.entries()].sort(([a], [b]) => (a < b ? 1 : -1));
    return ordered.map(([key, arr]) => ({
        label: humanDayLabel(key),
        items: arr,
    }));
}

function humanDayLabel(yyyy_mm_dd) {
    const d = new Date(yyyy_mm_dd);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    if (sameDay(d, today)) return "Today";
    if (sameDay(d, yest)) return "Yesterday";
    const opts = { weekday: "short", day: "2-digit", month: "short" };
    return d.toLocaleDateString(undefined, opts).toUpperCase();
}

function sameDay(a, b) {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}
